import SettingsModule from 'automation-ui-canvas/containers/settings/v2/SettingsModule';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import UIRadioInput from 'UIComponents/input/UIRadioInput';
import * as UnenrollmentTypes from 'automation-ui-canvas/constants/UnenrollmentTypes';
import UnenrollmentSettingModel from 'automation-ui-canvas/flow/records/properties/UnenrollmentSetting';
import UIFormControl from 'UIComponents/form/UIFormControl';
import { useCallback, useMemo, useState } from 'react';
import I18n from 'I18n';
import { List } from 'immutable';
import { Toggle } from 'foundations-components/transitional/components/Toggle';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { SUMMARIES_GET_ENABLED_WORKFLOWS } from 'automation-ui-canvas/queries/flowQueries';
import { useQuery } from 'data-fetching-client';
import UISelect from 'UIComponents/input/UISelect';
import UILink from 'UIComponents/link/UILink';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const UNENROLLMENT_WORKFLOW_PAGE_SIZE = 50;
const UnenrollmentSetting = ({
  readOnly,
  settings: {
    classicEnrollmentSettings
  },
  onChange
}) => {
  const trackEvent = useTrackEvent();
  const {
    fetchMore
  } = useQuery(SUMMARIES_GET_ENABLED_WORKFLOWS, {});
  const [options, setOptions] = useState([]);
  const [prevInput, setPrevInput] = useState('');
  const loadOptions = useCallback((input, callback, pagination = {
    offset: 0
  }) => {
    fetchMore({
      variables: {
        limit: UNENROLLMENT_WORKFLOW_PAGE_SIZE,
        offset: pagination.offset,
        name: input
      }
    }).then(({
      data
    }) => {
      const results = data ? data.summariesGetEnabledWorkflows.results : [];
      const newOptions = results.map(obj => ({
        text: obj.name,
        value: String(obj.migrationStatus.workflowId)
      }));
      setPrevInput(input);
      const updatedOptions = input === prevInput ? [...options, ...newOptions] : newOptions;
      setOptions(updatedOptions);
      callback(null, {
        options: updatedOptions,
        pagination: {
          hasMore: data ? data.summariesGetEnabledWorkflows.hasMore : false,
          length: updatedOptions.length,
          offset: data.summariesGetEnabledWorkflows.offset
        }
      });
    }).catch(err => {
      reportError({
        error: err
      });
    });
  }, [fetchMore, prevInput, options]);
  const unenrollmentSetting = useMemo(() => classicEnrollmentSettings && classicEnrollmentSettings.unenrollmentSetting || new UnenrollmentSettingModel({}), [classicEnrollmentSettings]);
  const handleUnenrollmentSettingTypeChange = useCallback(type => {
    if (classicEnrollmentSettings) {
      trackEvent(Trackables.WORKFLOW_SETTINGS_INTERACTION, {
        value: type,
        subAction: 'connectionsUnenrollment'
      });
      onChange({
        classicEnrollmentSettings: classicEnrollmentSettings.set('unenrollmentSetting', unenrollmentSetting.set('type', type))
      });
    }
  }, [classicEnrollmentSettings, onChange, unenrollmentSetting, trackEvent]);
  const handleUnenrollmentSettingWorkflowsChange = useCallback(workflowIds => {
    if (classicEnrollmentSettings) {
      onChange({
        classicEnrollmentSettings: classicEnrollmentSettings.set('unenrollmentSetting', unenrollmentSetting.set('excludedWorkflows', workflowIds))
      });
    }
  }, [classicEnrollmentSettings, onChange, unenrollmentSetting]);
  const value = unenrollmentSetting.excludedWorkflows ? unenrollmentSetting.excludedWorkflows.map(id => String(id)).toJS() : [];
  const hasUnenrollmentError = unenrollmentSetting.type === UnenrollmentTypes.SELECTIVE && unenrollmentSetting.excludedWorkflows.size === 0;
  const errorMessage = hasUnenrollmentError ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "settings.unenrollmentSetting.needsSelectionError"
  }) : '';
  return /*#__PURE__*/_jsx(SettingsModule, {
    header: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "settingsV2.unenrollmentSetting.title"
    }),
    body: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Toggle, {
        readOnly: readOnly,
        checked: !(unenrollmentSetting.type === UnenrollmentTypes.NONE || unenrollmentSetting.type === null),
        onChange: ({
          target: {
            checked
          }
        }) => {
          if (checked) {
            handleUnenrollmentSettingTypeChange(UnenrollmentTypes.ALL);
            return;
          }
          handleUnenrollmentSettingTypeChange(UnenrollmentTypes.NONE);
        },
        marginBlockEnd: "200"
      }), !(unenrollmentSetting.type === UnenrollmentTypes.NONE || unenrollmentSetting.type === null) && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(UIRadioInput, {
          name: "unenrollment",
          checked: unenrollmentSetting.type === UnenrollmentTypes.ALL,
          onChange: () => {
            handleUnenrollmentSettingTypeChange(UnenrollmentTypes.ALL);
          },
          readOnly: readOnly,
          "data-test-id": "platform-settings-unenrollment-all-radio",
          children: /*#__PURE__*/_jsx(UIHelpIcon, {
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "settings.unenrollmentSetting.allOption.tooltip"
            }),
            tooltipPlacement: "right",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "settingsV2.unenrollmentSetting.all"
            })
          })
        }), /*#__PURE__*/_jsx(UIRadioInput, {
          name: "unenrollment",
          checked: unenrollmentSetting.type === UnenrollmentTypes.SELECTIVE,
          onChange: () => {
            handleUnenrollmentSettingTypeChange(UnenrollmentTypes.SELECTIVE);
          },
          readOnly: readOnly,
          "data-test-id": "platform-settings-unenrollment-selective-radio",
          children: /*#__PURE__*/_jsx(UIHelpIcon, {
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "settings.unenrollmentSetting.customOption.tooltip"
            }),
            tooltipPlacement: "right",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "settingsV2.unenrollmentSetting.selective"
            })
          })
        }), unenrollmentSetting.type === UnenrollmentTypes.SELECTIVE && /*#__PURE__*/_jsx(UIFormControl, {
          error: hasUnenrollmentError,
          validationMessage: errorMessage,
          children: /*#__PURE__*/_jsx(UISelect, {
            onOpenChange: () => {
              setOptions([]);
            },
            cache: false,
            filterOptions: false,
            loadMoreComponent: /*#__PURE__*/_jsx(UILink, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "automation.enrollmentHistory.panel.selected-enrollment.loadMore"
              })
            }),
            loadOptions: loadOptions,
            placeholder: I18n.text('settings.unenrollmentSetting.selectWorkflow'),
            onChange: ({
              target: {
                value: newValue
              }
            }) => {
              const listValue = List(newValue.map(listId => {
                return Number(typeof listId === 'object' ? listId.value : listId);
              }));
              handleUnenrollmentSettingWorkflowsChange(listValue);
            },
            multi: true,
            readOnly: readOnly,
            anchorType: "button",
            multiCollapseLimit: 10,
            value: value
          })
        })]
      })]
    })
  });
};
export default UnenrollmentSetting;